import React, { useRef, useLayoutEffect } from 'react';
import { Bold, Italic, Underline, Strikethrough } from 'lucide-react';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Simple rich text editor with Bold, Italic, Underline, Strikethrough.
 * Stores content as HTML. Select text and use the toolbar to apply formatting.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter content...',
  minHeight = '200px',
  className = '',
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  // Initialize to '' so that when opening for edit, value (existing content) !== lastValueRef
  // and the effect will populate the editor; otherwise we'd never set innerHTML on first load.
  const lastValueRef = useRef<string>('');
  const isInternalChangeRef = useRef(false);

  // Sync value from parent into editor (e.g. when loading existing content).
  // useLayoutEffect runs before paint so the user never sees an empty box when editing.
  useLayoutEffect(() => {
    if (editorRef.current == null) return;
    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
    if (value !== lastValueRef.current) {
      // If value is plain text (no tags), convert newlines to <br/> for display
      const toShow = value || '';
      const html = /<[a-z][\s\S]*>/i.test(toShow)
        ? toShow
        : toShow.replace(/\n/g, '<br/>');
      editorRef.current.innerHTML = html;
      lastValueRef.current = value || '';
    }
  }, [value]);

  const emitChange = () => {
    if (!editorRef.current) return;
    const html = editorRef.current.innerHTML;
    lastValueRef.current = html;
    isInternalChangeRef.current = true;
    onChange(html);
  };

  const execCmd = (command: string, value?: string) => {
    document.execCommand(command, false, value);
    editorRef.current?.focus();
    emitChange();
  };

  return (
    <div className={`border border-gray-300 rounded-md overflow-hidden ${className}`}>
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 p-1 border-b border-gray-300 bg-gray-50">
        <button
          type="button"
          onClick={() => execCmd('bold')}
          className="p-2 rounded hover:bg-gray-200"
          title="Bold"
          aria-label="Bold"
        >
          <Bold className="w-4 h-4 text-gray-700" />
        </button>
        <button
          type="button"
          onClick={() => execCmd('italic')}
          className="p-2 rounded hover:bg-gray-200"
          title="Italic"
          aria-label="Italic"
        >
          <Italic className="w-4 h-4 text-gray-700" />
        </button>
        <button
          type="button"
          onClick={() => execCmd('underline')}
          className="p-2 rounded hover:bg-gray-200"
          title="Underline"
          aria-label="Underline"
        >
          <Underline className="w-4 h-4 text-gray-700" />
        </button>
        <button
          type="button"
          onClick={() => execCmd('strikeThrough')}
          className="p-2 rounded hover:bg-gray-200"
          title="Strikethrough"
          aria-label="Strikethrough"
        >
          <Strikethrough className="w-4 h-4 text-gray-700" />
        </button>
      </div>
      {/* Editable area */}
      <div
        ref={editorRef}
        contentEditable
        data-placeholder={placeholder}
        onInput={emitChange}
        onBlur={emitChange}
        className="p-3 outline-none min-h-[200px] prose prose-sm max-w-none focus:ring-0"
        style={{ minHeight }}
        suppressContentEditableWarning
      />
      <style>{`
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
        }
        [contenteditable] u { text-decoration: underline; }
        [contenteditable] s { text-decoration: line-through; }
      `}</style>
    </div>
  );
};

export default RichTextEditor;
